import { signatureUrlSchema, workSchedulesSchema } from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

export const getDoctorAttendingResponseSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid().nullable().default(null),
  firstname: z.string(),
  lastname: z.string(),
  phoneNumber: z.string().nullable().default(null),
  orderRegistrationNumber: z.string(),
  rhythm: z.string().nullable().default(null),
  // Structured rhythm fields
  onSiteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithoutAssistant: z.number().int().nullable().default(null),
  onSiteSlotsWithAssistant: z.number().int().nullable().default(null),
  remoteSlotsWithAssistant: z.number().int().nullable().default(null),
  rhythmNote: z.string().nullable().default(null),
  doctolibId: z.string().nullable().default(null),
  iban: z.string().nullable().default(null),
  bic: z.string().nullable().default(null),
  commissionRate: z.coerce.number().nullable(),
  signatureUrl: signatureUrlSchema,
  workSchedulesDateRef: z.string().nullable(),
  workSchedules: workSchedulesSchema,
  createdAt: z.coerce.date(),
  activatedAt: z.coerce.date().nullable().default(null),
  deactivatedAt: z.coerce.date().nullable().default(null),
});

export type GetDoctorAttendingResponse = z.infer<
  typeof getDoctorAttendingResponseSchema
>;

const getDoctorAttending = (id: string) => async () => {
  return http.doctorAttendings
    .getDetails(id)
    .then<GetDoctorAttendingResponse>(({ response, transform }) => {
      return transform.handle({
        response,
        attributes: {
          userId: true,
          firstname: true,
          lastname: true,
          phoneNumber: true,
          orderRegistrationNumber: true,
          rhythm: true,
          onSiteSlotsWithoutAssistant: true,
          remoteSlotsWithoutAssistant: true,
          onSiteSlotsWithAssistant: true,
          remoteSlotsWithAssistant: true,
          rhythmNote: true,
          doctolibId: true,
          iban: true,
          bic: true,
          commissionRate: true,
          signatureUrl: true,
          workSchedulesDateRef: true,
          workSchedules: true,
          createdAt: true,
          activatedAt: true,
          deactivatedAt: true,
        },
        schema: getDoctorAttendingResponseSchema,
      });
    });
};

export const useGetDoctorAttendingQuery = (
  id: string,
): UseQueryResult<GetDoctorAttendingResponse, Error> => {
  return useQuery({
    queryFn: getDoctorAttending(id),
    queryKey: ['doctor-attending', id],
    retry: false,
  });
};
